import { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Loader2, RefreshCw, ShieldCheck } from "lucide-react";

const SITEKEY = process.env.REACT_APP_RECAPTCHA_SITEKEY;
const REFRESH_MS = 100_000; // v3 tokens expire after 2 minutes — refresh before that
let loader = null;

const loadScript = () => {
  if (window.grecaptcha?.execute) return Promise.resolve();
  if (loader) return loader;
  loader = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITEKEY}`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { loader = null; s.remove(); reject(new Error("network")); };
    document.head.appendChild(s);
  });
  return loader;
};

// Fetch a fresh reCAPTCHA v3 token right before a protected request. Actions must start with "gfy_" (checked server-side).
export const getCaptchaToken = async (action = "gfy_form") => {
  if (!SITEKEY) return null;
  await loadScript();
  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => window.grecaptcha.execute(SITEKEY, { action }).then(resolve, reject));
  });
};

// Invisible reCAPTCHA v3 adapter. Keeps the old widget contract: `onVerify(token|null)`, `ref.reset()`, optional `active`.
// Mounts only once the form is touched, mints a token, keeps it fresh, and re-mints on reset() after each submit.
export const CaptchaBox = forwardRef(({ onVerify, active, action = "gfy_form" }, ref) => {
  const holder = useRef(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const cb = useRef(onVerify);
  cb.current = onVerify;
  const live = active !== undefined ? active : touched;

  const mint = useCallback(async () => {
    setBusy(true);
    try { const t = await getCaptchaToken(action); setErr(null); cb.current?.(t); } catch (e) { setErr(String(e?.message || e || "network")); cb.current?.(null); } finally { setBusy(false); }
  }, [action]);
  useImperativeHandle(ref, () => ({ reset: () => { cb.current?.(null); if (live) mint(); } }), [live, mint]);

  useEffect(() => {
    if (active !== undefined || touched) return;
    const form = holder.current?.closest("form");
    if (!form) { setTouched(true); return; }
    const on = () => setTouched(true);
    form.addEventListener("focusin", on); form.addEventListener("pointerdown", on);
    return () => { form.removeEventListener("focusin", on); form.removeEventListener("pointerdown", on); };
  }, [active, touched]);

  useEffect(() => {
    if (!live || !SITEKEY) return;
    mint();
    const id = setInterval(mint, REFRESH_MS);
    return () => clearInterval(id);
  }, [live, mint]);

  if (!SITEKEY) return null;
  if (!live) {
    return (
      <p ref={holder} className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-gf-muted" data-testid="captcha-idle">
        <ShieldCheck className="w-3.5 h-3.5 text-gf-primary" /> Invisible bot check runs when you start typing
      </p>
    );
  }
  return (
    <div ref={holder} className="mt-3" data-testid="captcha-box">
      {err ? (
        <button type="button" onClick={mint} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-amber-300 hover:text-gf-primary" data-testid="captcha-retry">
          <RefreshCw className="w-3 h-3" /> Security check couldn't load ({err}) — tap to retry
        </button>
      ) : (
        <p className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-wider text-gf-muted" data-testid="captcha-status">
          {busy ? <Loader2 className="w-3 h-3 animate-spin text-gf-primary" /> : <ShieldCheck className="w-3.5 h-3.5 text-gf-primary" />}
          Protected by reCAPTCHA · <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-gf-primary">Privacy</a> · <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-gf-primary">Terms</a>
        </p>
      )}
    </div>
  );
});

export const captchaRequired = () => !!SITEKEY;
